import { AxiosRequestConfig, AxiosResponse } from 'axios';
import { axiosInstance } from '../axios-instance';
import { DateTimeInterval, Location, LocationConfiguration } from '../quipli-service/location/location';
import { DateTime } from 'luxon';
import { CustomerProduct, ProductCustomService, ProductOption } from './product/customer-product';
import { LocationParsers } from '../quipli-service/location/location-parsers';
import { CustomerProductParsers } from './product/customer-product-parsers';
import { CalculatePriceRequest, CalculatePriceResult, ProductPrice } from '../quipli-service/pricing/pricing';
import { PricingParsers } from '../quipli-service/pricing/pricing-parsers';
import { TenantContext } from '../../context/tenant-context';
import { useContext } from 'react';
import { ConfigurationParsers } from '../quipli-service/configuration/configuration-parsers';
import { Configuration } from '../quipli-service/configuration/configuration';
import { CustomerCustomer, InsuranceDocument, InsuranceDocumentRequest } from './customer/customer-customer';
import { Pagination } from '../quipli-service/pagination/pagination';
import { useCustomerLocationApiV1 } from './location/customer-location-api-v1';
import { handleRequest } from '../quipli-service/quipli-service-client';
import { StripeCard } from '../quipli-service/payment/payment';
import { OnAccount } from '../quipli-service/payment/on-account/on-account';
import { OnAccountParsers } from '../quipli-service/payment/on-account/on-account-parsers';
import { PaymentParsers } from '../quipli-service/payment/payment-parsers';
import { CustomerOrder } from './order/customer-order';
import { CustomerOrderParsers } from './order/customer-order-parsers';
import { CustomerInvoiceParsers } from './invoice/customer-invoice-parsers';
import { Product, ProductImage, SearchProductsRequest } from '../quipli-service/product/product';
import { CustomerParsers } from './customer/customer-parsers';
import {
  UserAddress,
  UserAddressCreateRequest,
  UserDocument,
  UserDocumentType,
  UserDriverLic,
  UserDriverLicRequest,
  UserDriverLicScanRequesst,
  UserDriverLicScanResponse,
} from '../quipli-service/user/user';
import { Label } from '../quipli-service/label/label';
import { OrderType } from '../quipli-service/order/order';
import { UrlAlias, UrlAliasType } from './url-alias/url-alias';
import { useCustomerCartApi } from './cart/customer-cart-api';
import { ProductParsers } from '../quipli-service/product/product-parsers';
import { DeliveryLocation, DeliveryOption } from '../quipli-service/delivery/delivery';
import { CalculateFinancialSummaryRequest, FinancialSummary } from '../quipli-service/financials/financials';
import { FinancialsParsers } from '../quipli-service/financials/financials-parsers';
import { CouponParsers } from '../quipli-service/coupon/coupon-parsers';
import { UserAgreement } from '../quipli-service/agreement-documents/agreement-document';
import { AgreementDocumentsParsers } from '../quipli-service/agreement-documents/agreement-document-parsers';
import { Category } from '../quipli-service/category/category';
import { CategoryParsers } from '../quipli-service/category/category-parsers';
import { UserParsers } from '../quipli-service/user/user-parsers';
import { Theme } from '../quipli-service/theme/theme';
import { EmailMessage } from '../quipli-service/email/email';
import { CategoryFiltersParsers } from './category-filters/category-filters-parser';
import { FilterGroup } from './category-filters/category-filters';
import { PaginationParsers } from '../quipli-service/pagination/pagination-parsers';
import { StripeBankAccount } from '../quipli-service/payment/payment-v2';
import { CustomerPayment } from './invoice/customer-invoice';
import {
  InvoiceSearchSummaryData,
  SearchInvoiceRequest,
  StatementPaymentSummary,
} from '../quipli-service/invoice/invoice';
import { InvoiceParsers } from '../quipli-service/invoice/invoice-parsers';
import CustomerProductJson = CustomerProductParsers.CustomerProductJson;
import CustomerInsuranceJson = CustomerParsers.CustomerInsuranceJson;
import { StateProvince } from '../quipli-service/locales/locales';

export const useCustomerAPI = () => {
  const { tenant, contextPath } = useContext(TenantContext);

  const locationApi = useCustomerLocationApiV1();
  const cartApi = useCustomerCartApi();

  async function getRentalAvailability(
    productId: number,
    locationId: number,
    quantity: number,
  ): Promise<DateTimeInterval[]> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/customer-api/v1/availability/getRentalAvailability`,
      headers: {
        'X-Tenant': tenant,
      },
      data: {
        productId,
        locationId,
        quantity,
      },
    };
    const result = await handleCustomerRequest<{ start: string | undefined; end: string | undefined }[]>(config);
    return result.map((i) => {
      return {
        start: i.start ? DateTime.fromISO(i.start, { setZone: true }) : undefined,
        end: i.end ? DateTime.fromISO(i.end, { setZone: true }) : undefined,
      };
    });
  }

  async function getMultipleRentalAvailability(
    products: {
      productId: number;
      quantity: number;
    }[],
    locationId: number,
  ): Promise<{ productId: number; availability: DateTimeInterval[] }[]> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/customer-api/v1/availability/getMultipleRentalAvailability`,
      headers: {
        'X-Tenant': tenant,
      },
      data: {
        products,
        locationId,
      },
    };
    const result = await handleCustomerRequest<
      { productId: number; availability: { start: string | undefined; end: string | undefined }[] }[]
    >(config);
    return result.map((i) => {
      return {
        productId: i.productId,
        availability: i.availability.map((a) => {
          return {
            start: a.start ? DateTime.fromISO(a.start, { setZone: true }) : undefined,
            end: a.end ? DateTime.fromISO(a.end, { setZone: true }) : undefined,
          };
        }),
      };
    });
  }

  async function getAvailableCount(
    productId: number,
    locationId: number,
    from: DateTime,
    to: DateTime,
    type: OrderType,
  ): Promise<number> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/customer-api/v1/availability/getAvailableCount`,
      headers: {
        'X-Tenant': tenant,
      },
      data: {
        productId,
        locationId,
        from,
        to,
        type,
      },
    };
    return handleCustomerRequest<number>(config);
  }

  async function getProductById(productId: number): Promise<CustomerProduct> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/customer-api/v1/products/${productId}`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    const result = await handleCustomerRequest<CustomerProductJson>(config);
    return CustomerProductParsers.toCustomerProduct(result);
  }

  async function listPricesForMultipleProducts(
    productIds: number[],
    locationId?: number,
    type: OrderType | null = OrderType.Rental,
    userId?: number,
  ): Promise<Map<number, ProductPrice[]>> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/customer-api/v1/product/prices/multiple`,
      headers: {
        'X-Tenant': tenant,
      },
      data: {
        productIds,
        userId,
        locationId,
        type,
        sortBy: 'price',
      },
    };
    const result = await handleCustomerRequest<Record<string, PricingParsers.ProductPriceJson[]>>(config);
    const resultMap = new Map<number, ProductPrice[]>();
    Object.keys(result).map((productId) => {
      resultMap.set(parseInt(productId), result[productId].map(PricingParsers.toProductPrice));
    });
    return resultMap;
  }

  async function calculatePrices(data: CalculatePriceRequest[]): Promise<CalculatePriceResult[]> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/customer-api/v1/product/prices/calculatePrice`,
      headers: {
        'X-Tenant': tenant,
      },
      data: data.map((d) => {
        return {
          ...d,
          ...{
            start: d.type === OrderType.Rental ? d.start!.toISO() : undefined,
            end: d.type === OrderType.Rental ? d.end!.toISO() : undefined,
          },
        };
      }),
    };
    return await handleCustomerRequest<CalculatePriceResult[]>(config);
  }

  async function userContext(): Promise<CustomerCustomer | undefined> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/customer-api/v1/user/context`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    const result = await handleCustomerRequest<CustomerCustomer | undefined>(config);
    return result;
  }

  async function getConfigurations(configurationNames: string[]): Promise<Configuration[]> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/customer-api/v1/configurations/multiple`,
      headers: {
        'X-Tenant': tenant,
      },
      data: configurationNames,
    };
    const result = await handleCustomerRequest<ConfigurationParsers.ConfigurationJson[]>(config);
    return result.map(ConfigurationParsers.toConfiguration);
  }

  async function getConfiguration(configurationName: string): Promise<Configuration | undefined> {
    const configs = await getConfigurations([configurationName]);
    return configs[0];
  }

  async function getLocationConfiguration(
    locationId: number,
    configurationName: string,
  ): Promise<LocationConfiguration | undefined> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/customer-api/v1/locations/${locationId}/configuration/${configurationName}`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    const result = await handleCustomerRequest<LocationParsers.LocationConfigurationJson | undefined>(config);
    return result && LocationParsers.toLocationConfiguration(result);
  }

  async function getLocationConfigurations(
    locationId: number,
    configurationNames: string[],
  ): Promise<LocationConfiguration[]> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/customer-api/v1/locations/${locationId}/configuration`,
      headers: {
        'X-Tenant': tenant,
      },
      data: configurationNames,
    };
    const result = await handleCustomerRequest<LocationParsers.LocationConfigurationJson[]>(config);
    return result.map(LocationParsers.toLocationConfiguration);
  }

  async function addTerminalCard(
    userId: number,
    locationId: number,
  ): Promise<{ setupIntentId: string; status: string }> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/customer-api/v1/payments/stripe/terminal/${locationId}`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    return await handleRequest<{ setupIntentId: string; status: string }>(config);
  }

  async function completeCardSetup(
    userId: number,
    setupIntentId: string,
  ): Promise<{ setupIntentId: string; status: string }> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/payments/stripe/terminal/completeSetup`,
      data: { userId, setupIntentId },
      headers: {
        'X-Tenant': tenant,
      },
    };
    return await handleCustomerRequest<{ setupIntentId: string; status: string }>(config);
  }

  async function getUserBankDetails(): Promise<OnAccount | undefined> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/customer-api/v1/payments/onAccount/bankDetails`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    const result = await handleRequest<OnAccountParsers.OnAccountJson | undefined>(config);

    return result ? OnAccountParsers.toOnAccount(result) : undefined;
  }

  async function requestOnAccount(): Promise<OnAccount> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/customer-api/v1/payments/onAccount/request`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    const result = await handleRequest<OnAccountParsers.OnAccountJson>(config);
    return OnAccountParsers.toOnAccount(result);
  }

  async function getUserCards(): Promise<StripeCard[]> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/customer-api/v1/payments/cards`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    const json = await handleRequest<PaymentParsers.StripeCardJson[]>(config);
    return json.map(PaymentParsers.toStripeCard);
  }

  async function deleteUserCard(cardId: number): Promise<number> {
    const config = {
      method: 'delete',
      url: `${contextPath}quipli-service/customer-api/v1/payments/cards/${cardId}`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    return await handleRequest<number>(config);
  }

  async function getOrderById(orderId: number): Promise<CustomerOrder> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/customer-api/v1/orders/${orderId}`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    const json = await handleRequest<CustomerOrderParsers.CustomerOrderJson>(config);
    return CustomerOrderParsers.toCustomerOrder(json);
  }

  async function refreshPayments(orderId: number): Promise<CustomerPayment[]> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/customer-api/v2/payments/refresh/${orderId}`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    const json = await handleRequest<CustomerInvoiceParsers.CustomerPaymentJson[]>(config);
    return json.map(CustomerInvoiceParsers.toCustomerPayment);
  }

  async function getPublicBucket(): Promise<string> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/customer-api/v1/publicBucket`,
    };
    return await handleRequest<string>(config);
  }

  async function getProductImages(
    productId: number,
    size: 'MINI' | 'THUMB' | 'MEDIUM' | 'APP' | 'LARGE' | 'ORIGINAL' = 'THUMB',
    includeDownloadUrl: boolean = true,
  ): Promise<ProductImage[]> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/customer-api/v1/products/${productId}/images?size=${size}&includeDownloadUrls=${includeDownloadUrl}`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    return await handleRequest<ProductImage[]>(config);
  }

  async function createUpdateUserDocument(
    file: File,
    userId: string,
    userDocumentType: string,
    deleteExisting: boolean = false,
  ) {
    const data = new FormData();
    data.append('file', file);
    data.append('user_id', userId);
    data.append('user_document_type', userDocumentType);
    data.append('delete_existing_file', deleteExisting.toString());

    const config = {
      method: 'post',
      url: `${contextPath}cart/uploadUserDocument`,
      headers: {
        'X-Tenant': tenant,
        'Legacy-Route': true,
      },
      data: data,
      cache: false,
      contentType: false,
      processData: false,
      mimeType: 'multipart/form-data',
    };

    return await handleCustomerRequest<{ status: number; msg: string; fileId?: number }>(config, true);
  }

  async function addInsuranceDocument(body: Omit<InsuranceDocumentRequest, 'userId'>): Promise<InsuranceDocument> {
    console.log('body', {
      ...body,
      expiryDate: body.expiryDate?.toISODate(),
      issuedDate: body.issuedDate?.toISODate(),
    });
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/customer-api/v1/user/insurance`,
      headers: {
        'X-Tenant': tenant,
      },
      data: { ...body, expiryDate: body.expiryDate?.toISODate(), issuedDate: body.issuedDate?.toISODate() },
    };
    return CustomerParsers.toCustomerInsurance(await handleCustomerRequest<CustomerInsuranceJson>(config));
  }

  async function updateInsuranceDocument(
    documentId: number,
    body: Omit<InsuranceDocumentRequest, 'userId'>,
  ): Promise<InsuranceDocument> {
    console.log('body', {
      ...body,
      expiryDate: body.expiryDate?.toISODate(),
      issuedDate: body.issuedDate?.toISODate(),
    });

    const config = {
      method: 'put',
      url: `${contextPath}quipli-service/customer-api/v1/user/insurance/${documentId}`,
      headers: {
        'X-Tenant': tenant,
      },
      data: { ...body, expiryDate: body.expiryDate?.toISODate(), issuedDate: body.issuedDate?.toISODate() },
    };
    return CustomerParsers.toCustomerInsurance(await handleCustomerRequest<CustomerInsuranceJson>(config));
  }

  async function deleteInsuranceDocument(documentId: number): Promise<void> {
    const config = {
      method: 'delete',
      url: `${contextPath}quipli-service/customer-api/v1/user/insurance/${documentId}`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    return await handleCustomerRequest<void>(config);
  }

  async function getInsuranceDocuments(includeDeleted: boolean = false): Promise<InsuranceDocument[]> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/customer-api/v1/user/insurance${includeDeleted ? '?includeDeleted=1' : ''}`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    const result = await handleCustomerRequest<CustomerInsuranceJson[]>(config);
    return result.map(CustomerParsers.toCustomerInsurance);
  }

  async function getRelatedProducts(productId: number): Promise<CustomerProduct[]> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/customer-api/v1/products/${productId}/relatedProducts`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    const result = await handleCustomerRequest<CustomerProductJson[]>(config);
    return result.map(CustomerProductParsers.toCustomerProduct);
  }

  async function getProductAddOns(productId: number, locationId: number): Promise<CustomerProduct[]> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/customer-api/v1/products/${productId}/addons?locationId=${locationId}`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    const result = await handleCustomerRequest<CustomerProductJson[]>(config);
    return result.map(CustomerProductParsers.toCustomerProduct);
  }

  async function getProductCustomizations(
    productId: number,
  ): Promise<{ options: ProductOption[]; services: ProductCustomService[] }> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/customer-api/v1/products/${productId}/customizations`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    const result = await handleCustomerRequest<CustomerProductParsers.CustomizationsJson>(config);
    return CustomerProductParsers.toCustomerProductCustomizations(result);
  }

  async function getMultipleLabels(labels: string[]): Promise<Label[]> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/customer-api/v1/labels/multiple`,
      headers: {
        'X-Tenant': tenant,
      },
      data: labels,
    };
    return await handleRequest<Label[]>(config);
  }

  async function getLocation(locationId: number): Promise<Location> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/customer-api/v1/locations/${locationId}`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    const res = await handleRequest<LocationParsers.LocationJson>(config);
    return LocationParsers.toLocation(res);
  }

  async function getUrlAliases(type: UrlAliasType, entityIds: number[]): Promise<UrlAlias[]> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/customer-api/v1/url-alias/multiple`,
      data: {
        type,
        entityIds,
      },
    };
    return await handleRequest<UrlAlias[]>(config);
  }

  async function getMultipleProductsByIds(ids: number[]): Promise<Product[]> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/customer-api/v1/products/multiple`,
      headers: {
        'X-Tenant': tenant,
      },
      data: ids,
    };

    const result = await handleCustomerRequest<ProductParsers.ProductJson[]>(config);
    return result.map(ProductParsers.toProduct);
  }

  async function calculateFinancialSummary(
    request: Omit<CalculateFinancialSummaryRequest, 'customerId'>,
  ): Promise<FinancialSummary> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/customer-api/v1/financials/calculateFinancialSummary`,
      data: request,
      headers: {
        'X-Tenant': tenant,
        'X-Location-Id': request.locationId,
      },
    };

    const result = await handleCustomerRequest<FinancialsParsers.FinancialSummaryJson>(config);
    return FinancialsParsers.toFinancialSummary(result);
  }

  async function getCouponByCode(code: string) {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/customer-api/v1/coupons/${code}`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    const result = await handleCustomerRequest<CouponParsers.CouponJson | undefined>(config);
    return result && CouponParsers.toCoupon(result);
  }

  async function getUserAddresses(): Promise<UserAddress[]> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/customer-api/v1/user/addresses`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    return await handleCustomerRequest<UserAddress[]>(config);
  }

  async function createUserAddress(body: Omit<UserAddressCreateRequest, 'userId'>): Promise<UserAddress> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/customer-api/v1/user/addresses`,
      headers: {
        'X-Tenant': tenant,
      },
      data: body,
    };
    return await handleCustomerRequest<UserAddress>(config);
  }

  async function updateUserAddress(
    adddressId: number,
    body: Omit<UserAddressCreateRequest, 'userId'>,
  ): Promise<UserAddress> {
    const config = {
      method: 'put',
      url: `${contextPath}quipli-service/customer-api/v1/user/addresses/${adddressId}`,
      headers: {
        'X-Tenant': tenant,
      },
      data: body,
    };
    return await handleCustomerRequest<UserAddress>(config);
  }

  async function deleteUserAddress(addressId: number) {
    const config = {
      method: 'delete',
      url: `${contextPath}quipli-service/customer-api/v1/user/addresses/${addressId}`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    return await handleCustomerRequest(config);
  }

  async function listUserAgreements(orderId?: number): Promise<UserAgreement[]> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/customer-api/v1/user/agreements${orderId ? `?orderId=${orderId}` : ''}`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    const res = await handleCustomerRequest<AgreementDocumentsParsers.UserAgreementJson[]>(config);
    return res.map(AgreementDocumentsParsers.toUserAgreement);
  }

  async function listDeliveryLocations(): Promise<DeliveryLocation[]> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/customer-api/v1/delivery/locations`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    return await handleCustomerRequest<DeliveryLocation[]>(config);
  }

  async function getMultipleCategoriesByIds(categoryIds: number[]): Promise<Category[]> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/customer-api/v1/categories/multiple`,
      headers: {
        'X-Tenant': tenant,
      },
      data: categoryIds,
    };

    const result = await handleCustomerRequest<CategoryParsers.CategoryJson[]>(config);
    return result.map((r) => CategoryParsers.toCategory(r));
  }

  async function downloadUserDocumentFile(documentId: number) {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/customer-api/v1/user/documents/${documentId}/download`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    return await handleRequest<string>(config);
  }

  async function getUserDocumentByType(documentType: UserDocumentType): Promise<UserDocument | undefined> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/customer-api/v1/user/documents/${documentType}`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    return handleCustomerRequest<UserDocument | undefined>(config);
  }

  async function getUserDocuments(): Promise<UserDocument[]> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/customer-api/v1/user/documents`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    return handleCustomerRequest<UserDocument[]>(config);
  }

  async function getUserDriverLicense(): Promise<UserDriverLic | undefined> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/customer-api/v1/user/driverLicense`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    const response = await handleCustomerRequest<UserParsers.UserDriverLicenseJson>(config);
    return response && UserParsers.toUserDriverLicense(response);
  }

  async function createUserDriverLicense(body: UserDriverLicRequest): Promise<{ isValid: boolean; error: string }> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/customer-api/v1/user/driverLicense`,
      headers: {
        'X-Tenant': tenant,
      },
      data: body,
    };
    return await handleCustomerRequest<{ isValid: boolean; error: string }>(config);
  }

  async function updateUserDriverLicense(body: UserDriverLicRequest): Promise<number> {
    const config = {
      method: 'put',
      url: `${contextPath}quipli-service/customer-api/v1/user/driverLicense`,
      headers: {
        'X-Tenant': tenant,
      },
      data: body,
    };
    return await handleCustomerRequest<number>(config);
  }

  async function scanUserDriverLicense(body: UserDriverLicScanRequesst): Promise<UserDriverLicScanResponse> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/customer-api/v1/user/driverLicense/scan`,
      headers: {
        'X-Tenant': tenant,
      },
      data: body,
    };

    const response = await handleCustomerRequest<UserParsers.UserDriverLicenseScanJson>(config);
    return response && UserParsers.toUserDriverLicenseScan(response);
  }

  async function listStateProvinces(countryId?: number): Promise<StateProvince[]> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/customer-api/v1/state-provinces/list`,
      data: { countryIds: [countryId] },
    };

    return handleCustomerRequest<StateProvince[]>(config);
  }

  async function getActiveTheme(): Promise<Theme | undefined> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/customer-api/v1/themes/active`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    return await handleCustomerRequest<Theme | undefined>(config);
  }

  async function getFeaturedProducts(locationId?: number): Promise<CustomerProduct[]> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/customer-api/v1/products/featured${
        locationId ? `&locationId=${locationId}` : ''
      }`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    return await handleCustomerRequest<CustomerProduct[]>(config);
  }
  async function sendEmailAsync(msg: EmailMessage): Promise<void> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/customer-api/v1/emails/send`,
      headers: {
        'X-Tenant': tenant,
      },
      data: msg,
    };
    return await handleRequest(config);
  }

  async function searchProducts(data: SearchProductsRequest): Promise<Pagination<CustomerProduct>> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/customer-api/v1/products/search`,
      headers: {
        'X-Tenant': tenant,
      },
      data,
    };
    const { items, pagination } = await handleRequest<
      PaginationParsers.PaginationJson<CustomerProductParsers.CustomerProductJson>
    >(config);
    return {
      pagination,
      items: items.map(CustomerProductParsers.toCustomerProduct),
    };
  }

  async function getCategoryByName(name: string): Promise<Category | undefined> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/customer-api/v1/categories/byName/${name}`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    const result = await handleCustomerRequest<CategoryParsers.CategoryJson | undefined>(config);
    return result && CategoryParsers.toCategory(result);
  }

  async function getCategoryFilters(categoryId: number): Promise<FilterGroup[]> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/customer-api/v1/categories/${categoryId}/filters`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    const result = await handleCustomerRequest<CategoryFiltersParsers.FilterGroupJson[]>(config);
    return result.map(CategoryFiltersParsers.toCategoryFilter);
  }

  async function addBankAccount(params: {
    userId: number;
    stripePaymentMethodId: string;
    accountHolderName: string;
    status: string;
  }) {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/customer-api/v2/payments/methods/bank`,
      headers: {
        'X-Tenant': tenant,
      },
      data: params,
    };
    return await handleRequest<StripeBankAccount>(config);
  }

  async function deleteBankAccount(bankAccountId: string) {
    const config = {
      method: 'delete',
      url: `${contextPath}quipli-service/customer-api/v2/payments/methods/bank/${bankAccountId}`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    return await handleRequest<void>(config);
  }

  async function searchBankAccounts(params: { userIds?: number[]; stripePaymentMethodIds?: string[]; ids?: string[] }) {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/customer-api/v2/payments/methods/bank/search`,
      headers: {
        'X-Tenant': tenant,
      },
      data: params,
    };
    return await handleRequest<StripeBankAccount[]>(config);
  }

  async function searchSummaryData(request: SearchInvoiceRequest): Promise<InvoiceSearchSummaryData> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/customer-api/v1/invoices/searchSummaryData`,
      data: request,
      headers: {
        'X-Tenant': tenant,
        'X-Location-Id': request.locationId,
      },
    };

    return await handleCustomerRequest<InvoiceSearchSummaryData>(config);
  }

  async function getStatementPaymentSummary(req: {
    amount: number;
    userId: number;
  }): Promise<StatementPaymentSummary[]> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/customer-api/v1/invoices/statementPaymentSummary`,
      headers: {
        'X-Tenant': tenant,
      },
      data: req,
    };
    const sp = await handleRequest<InvoiceParsers.StatementPaymentSummaryJson[]>(config);
    return sp.map(InvoiceParsers.toStatementPaymentSummary);
  }

  async function getDeliveryOptions(
    productTypes: { id: number; type: OrderType }[],
    locationId: number,
  ): Promise<DeliveryOption[]> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/customer-api/v1/delivery/getDeliveryOptions`,
      headers: {
        'X-Tenant': tenant,
        'X-Location-Id': locationId,
      },
      data: {
        productTypes,
      },
    };
    return await handleCustomerRequest<DeliveryOption[]>(config);
  }

  return {
    ...locationApi,
    ...cartApi,
    getRentalAvailability,
    getMultipleRentalAvailability,
    getAvailableCount,
    getProductById,
    listPricesForMultipleProducts,
    calculatePrices,
    userContext,
    getConfiguration,
    getConfigurations,
    getLocationConfiguration,
    getLocationConfigurations,
    addTerminalCard,
    completeCardSetup,
    getUserBankDetails,
    requestOnAccount,
    getUserCards,
    deleteUserCard,
    getOrderById,
    refreshPayments,
    getPublicBucket,
    getProductImages,
    createUpdateUserDocument,
    addInsuranceDocument,
    updateInsuranceDocument,
    deleteInsuranceDocument,
    getInsuranceDocuments,
    getRelatedProducts,
    getProductAddOns,
    getProductCustomizations,
    getMultipleLabels,
    getLocation,
    getUrlAliases,
    getUserAddresses,
    getMultipleProductsByIds,
    calculateFinancialSummary,
    getCouponByCode,
    createUserAddress,
    updateUserAddress,
    deleteUserAddress,
    listUserAgreements,
    listDeliveryLocations,
    getMultipleCategoriesByIds,
    downloadUserDocumentFile,
    getUserDocumentByType,
    getUserDocuments,
    getUserDriverLicense,
    listStateProvinces,
    createUserDriverLicense,
    updateUserDriverLicense,
    scanUserDriverLicense,
    getActiveTheme,
    getFeaturedProducts,
    sendEmailAsync,
    searchProducts,
    getCategoryByName,
    getCategoryFilters,
    addBankAccount,
    searchBankAccounts,
    deleteBankAccount,
    searchSummaryData,
    getStatementPaymentSummary,
    getDeliveryOptions,
  };
};

async function withLoginRedirect(req: AxiosRequestConfig): Promise<AxiosResponse<any, any>> {
  try {
    return await axiosInstance(req);
  } catch (e: any) {
    if (e.status === 401) {
      const params = new URLSearchParams();
      params.set('dest', window.location.pathname + window.location.search);
      const contextPath = '';
      window.location.href = `${contextPath}/user/login?${params}`;
    }
    throw e;
  }
}

export async function handleCustomerRequest<T>(req: AxiosRequestConfig, isFile = false): Promise<T> {
  const response = await withLoginRedirect(req);
  return isFile ? response.data : response.data.data;
}

export async function handleCustomerPaginatedRequest<T>(req: AxiosRequestConfig): Promise<Pagination<T>> {
  const response = await withLoginRedirect(req);
  return { items: response.data.data, pagination: response.data.meta };
}
